import { Dispatch, SetStateAction, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAtomValue } from "jotai";

import {
  GCDriveHookProps,
  HandleTranscribeProps,
  RecordFileCaptureProps,
  UpdateGCComponent,
} from "../../GrowthDriveInterfaces";
import {
  canBeIndexed,
  IndexFiles,
} from "../../../../components/Drawers/CommentsDrawer/BrieChatWithGC/BrieChatWithGCHelpers";
import useDeleteRecordFile from "../../../../api/api-hooks/images/useDeleteRecordFile";
import {
  BuildGCDriveFilters,
  captureCamera,
} from "../../GrowthDriveUtils/DriveUtils";
import useQueriedRecords from "../../../../api/api-hooks/records/useQueriedRecords";
import {
  FileSources,
  FileWithDefaults,
} from "../../../../types/FileInterfaces";
import useUpdateRecord from "../../../../api/api-hooks/records/useUpdateRecord";
import useUpdateFile from "../../../../api/api-hooks/images/useUpdateFile";
import { callFunctionIfExists } from "../../../../utils/GeneralUtils";
import useRecord from "../../../../api/api-hooks/records/useRecord";
import { FileInterface } from "../../../../types/RecordInterfaces";
import { tenantIDAtom } from "../../../../atoms/GlobalAtoms";
import { AppConfirm } from "../../../../utils/PopupUtil";
import TenantAPI from "../../../../api/api/TenantAPI";
import API from "../../../../api/api/API";

export default function useGCDrive({
  parentRecordID,
  moduleAPIName,
  fieldAPIName,
  query,
  folderID,
  fileType,
  fileTypes,
  isPrivate,
  filesToFilter,
}: GCDriveHookProps) {
  const navigate = useNavigate();
  const client = useQueryClient();
  const { updateFile, updatingFile } = useUpdateFile();
  const { deleteRecordFile } = useDeleteRecordFile();
  const tenantID = useAtomValue(tenantIDAtom);
  const parentRecord = useRecord({
    moduleAPIName,
    recordID: parentRecordID,
    enabled: !!parentRecordID,
  });
  const { updateRecord, recordUpdating } = useUpdateRecord(
    false,
    parentRecordID
  );

  // Would be good to have an endpoint with the same behavior for Adding/Removing/Updating references but for files?
  // In that endpoint we can remove the record reference as well instead of doing another API call.

  const topLevelFilters = useMemo(() => {
    return BuildGCDriveFilters({
      recordID: parentRecordID,
      moduleAPIName: moduleAPIName,
      fieldAPIName: fieldAPIName,
      folderID: folderID,
      fileTypes: fileTypes,
      fileType: fileType,
      isPrivate: isPrivate,
      filesToFilter,
    });
  }, [query, folderID, parentRecord.record, fileType, isPrivate]);

  // Fetch the records
  const { records, pagination, recordsLoading, error } = useQueriedRecords({
    moduleAPIName: "Files",
    query,
    topLevelFilters: topLevelFilters,
    queryKeyID: !!parentRecord?.record
      ? `${parentRecordID}-${moduleAPIName}-${fieldAPIName}`
      : undefined,
  });

  /**
   * Handle Update
   *
   * Function which handles the update for:
   * - Files: Name and Caption properties.
   * - Folders: Name property.
   *
   * //TODO: Check if would be good to create a custom endpoint for this.
   */

  const handleUpdateSources = ({
    fileID,
    sources,
  }: {
    fileID: string;
    sources: FileSources[];
  }) => {
    updateFile({
      fileID,
      payload: {
        Sources: sources,
      },
    });
  };

  /**
   * Generic function to update different components in the Growth Drive.
   * // TODO: Support parentRecordID for the File Gallery.
   */
  const handleUpdateComponent = ({
    type,
    componentID,
    payload,
    onSuccess,
  }: UpdateGCComponent) => {
    switch (type) {
      case "File":
        updateFile({
          fileID: componentID,
          payload: payload,
          onSuccess: onSuccess && onSuccess,
        });
        break;

      case "Folder":
        updateFile({
          fileID: componentID,
          payload: payload,
          onSuccess: onSuccess && onSuccess,
        });
        break;
    }
  };

  /**
   * Generic function to handle file deletion.
   *
   * Handles the deletion for:
   * - Files.
   * - Folders.
   */
  const handleDelete = (id: string, type: "File" | "Folder") => {
    if (type === "File") {
      deleteRecordFile({
        body: {
          fileID: id,
          moduleAPIName: moduleAPIName || "",
          recordID: parentRecordID || "",
          fieldAPIName: fieldAPIName || "",
          type: "fileID",
        },
        onSuccess: () => {
          if (parentRecordID && moduleAPIName && fieldAPIName) {
            const attachments: any[] =
              parentRecord.record?.[fieldAPIName] || [];
            const newAttachments = attachments.filter(
              (a: any) => a?.RecordID !== id
            );

            updateRecord({
              field: {
                ReadOnly: false,
              },
              moduleAPIName,
              recordID: parentRecordID,
              payload: {
                [fieldAPIName]: newAttachments,
              },
              _UserActivity: null,
            });
          }
        },
      });
    }
  };

  /**
   * Helper function to handle transcribing a file.
   */
  const handleTranscribe = async ({
    fileID,
    file,
    setTranscribing,
  }: HandleTranscribeProps) => {
    setTranscribing(true);

    const fileBlobPath = file.File;
    let fileURL = file?.PublicURL;

    if (!fileURL) {
      const fileURLResponse = await TenantAPI.getFileURL(fileBlobPath);
      fileURL = fileURLResponse?.data?.data?.url;
    }

    await API.getQuickTranscript({
      FileURL: fileURL,
    })
      .then((response) => {
        const transcription = response.data.data?.transcription;
        handleUpdateComponent({
          type: "File",
          componentID: fileID,
          payload: {
            Transcript: transcription,
          },
        });
      })
      .finally(() => {
        setTranscribing(false);
      });
  };

  async function handleIndexFiles(
    setIndexing: Dispatch<SetStateAction<boolean>>
  ) {
    if (
      !parentRecordID ||
      !moduleAPIName ||
      !fieldAPIName ||
      !parentRecord.record
    )
      return;

    const files = (records as any[])?.flatMap((r) => {
      if (!r?.File) return [];

      return [{ ...r.File, RecordID: r._id }];
    });

    const filesToIndex = files?.filter(canBeIndexed);
    const indexedFiles = filesToIndex?.filter(
      (a: any) => a.Index || a?.hasOwnProperty("Transcript")
    );
    const nonIndexedFiles = filesToIndex?.filter(
      (a: any) => !a.Index && !a?.hasOwnProperty("Transcript")
    );

    if (!indexedFiles?.length && !filesToIndex?.length) {
      return AppConfirm({
        title: "No Files to Index",
        text: "All added files are not supported for indexing.",
        confirmButtonText: "OK",
      });
    }

    if (indexedFiles?.length === filesToIndex?.length) {
      return AppConfirm({
        title: "Files Already Indexed",
        text: "All suppported files have already been indexed.",
        confirmButtonText: "OK",
      });
    }

    const result = await AppConfirm({
      title: "Index Files",
      text: `There are ${nonIndexedFiles?.length} supported file(s) that have not been indexed. Would you like to index them now?`,
      confirmButtonText: "Index Files",
      confirmButtonColor: "var(--primary)",
    });

    if (!result.isConfirmed) return;

    setIndexing(true);

    await IndexFiles(
      nonIndexedFiles.map((f: any) => ({
        file: f,
        fieldAPIName: fieldAPIName,
      })),
      parentRecordID,
      moduleAPIName,
      navigate,
      tenantID!,
      client
    );

    setIndexing(false);
  }

  /**
   * Helper function to handle adding files from `Image Gallery` component.
   * @param recordFileCapture
   * @returns
   */
  async function handleRecordFileCapture(
    recordFileCapture: RecordFileCaptureProps
  ) {
    if (!parentRecordID || !moduleAPIName || !fieldAPIName) return;
    let newFiles: FileInterface[] = [];

    if (recordFileCapture.type === "Custom") {
      const target = recordFileCapture.target;

      const file = await captureCamera({
        target,
        privateContainer: !!isPrivate,
        fileSources: [
          {
            Source: "Record",
            SourceID: parentRecordID,
            SourceModule: moduleAPIName,
            FieldAPIName: fieldAPIName,
          },
        ],
      });

      if (!file) return;

      newFiles.push(file);
    } else {
      const files = recordFileCapture.files;

      newFiles = !Array.isArray(files) ? [files] : files;
    }

    let attachments = parentRecord.record?.[fieldAPIName];

    if (!attachments || !attachments.length) {
      attachments = [];
    }

    attachments.push(...newFiles);

    updateRecord({
      field: {
        ReadOnly: false,
      },
      moduleAPIName,
      recordID: parentRecordID,
      payload: {
        [fieldAPIName]: attachments,
      },
      _UserActivity: null,
      onSuccess: () => {
        callFunctionIfExists(recordFileCapture?.onSuccess);
        client.invalidateQueries({
          queryKey: [tenantID, "records", "Files"],
        });
      },
    });
  }

  return {
    handleRecordFileCapture,
    handleUpdateComponent,
    handleUpdateSources,
    handleTranscribe,
    handleIndexFiles,
    handleDelete,
    parentRecord,
    updatingFile: updatingFile || recordUpdating,
    records: records as FileWithDefaults[],
    pagination,
    recordsLoading,
    queryError: error,
  };
}
